import { useEffect, useMemo, useState } from 'react';
import type { SankeyGraph } from 'd3-sankey';
import {
  computeHighlightedFlow,
  type SiteDataFlowGraph,
  type SiteDataFlowSankeyNode,
  type SiteDataFlowSankeyLink,
} from '@/lib/site-data-flow-graph';
import { PAYLOAD_CATEGORY_EDGE_COLOR } from '@/lib/payload-category-palette';

interface DataFlowSankeyProps {
  graph: SiteDataFlowGraph;
  onSelectNode?: (node: SiteDataFlowSankeyNode) => void;
}

const WIDTH = 960;
const HEIGHT = 540;
const PADDING = 24;

type SankeyModule = typeof import('d3-sankey');

interface SankeyLinkInput {
  source: string;
  target: string;
  value: number;
  edge: SiteDataFlowSankeyLink;
}

// d3-sankey is loaded lazily so the recipients table renders without
// waiting on the chart bundle. Phase 3b CLAUDE.md rule: only the
// `d3-sankey` module — never the d3 umbrella.
export function DataFlowSankey({ graph, onSelectNode }: DataFlowSankeyProps) {
  const [d3Sankey, setD3Sankey] = useState<SankeyModule | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  // Pin wins over hover so the highlight stays anchored when the
  // reviewer mouses toward the legend or the recipients table.
  const activeId = pinnedId ?? hoveredId;

  useEffect(() => {
    let active = true;
    import('d3-sankey').then((mod) => {
      if (active) setD3Sankey(mod);
    });
    return () => {
      active = false;
    };
  }, []);

  const layout = useMemo(() => {
    if (!d3Sankey) return null;
    if (!graph.hasFlows) return null;
    return computeLayout(d3Sankey, graph);
  }, [d3Sankey, graph]);

  const highlight = useMemo(() => {
    if (!activeId) return null;
    return computeHighlightedFlow(activeId, graph.sankeyLinks);
  }, [activeId, graph.sankeyLinks]);

  if (!graph.hasFlows) {
    return (
      <div
        className="rounded-md border bg-background p-6 text-sm text-muted-foreground"
        data-testid="data-flow-empty"
      >
        This site has no observed pixel-piggyback chains.
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="rounded-md border bg-background p-6 text-sm text-muted-foreground">
        Loading chain map…
      </div>
    );
  }

  const linkPath = d3Sankey!.sankeyLinkHorizontal<
    SiteDataFlowSankeyNode,
    SankeyLinkInput
  >();

  // Hover/pin highlighting is scoped to page nodes — they're the
  // canonical "one scan = one row" anchor for the consent-mode story,
  // and lighting up vendors mid-chain on accidental hover is noisy.
  const handleNodeClick = (node: SiteDataFlowSankeyNode) => {
    if (node.kind === 'page') {
      setPinnedId((prev) => (prev === node.id ? null : node.id));
    }
    onSelectNode?.(node);
  };

  return (
    <svg
      role="img"
      aria-label="Site data flow"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full max-w-full rounded-md border bg-white"
      data-testid="data-flow-sankey"
      data-active-node={activeId ?? ''}
      data-pinned-node={pinnedId ?? ''}
      onClick={(e) => {
        if (e.target === e.currentTarget) setPinnedId(null);
      }}
    >
      <g fill="none">
        {layout.links.map((l, idx) => {
          const d = linkPath(l);
          if (!d) return null;
          const edge = l.edge;
          const color = PAYLOAD_CATEGORY_EDGE_COLOR[edge.dominantCategory];
          const thickness = Math.max(1, l.width ?? 1);
          const isHighlighted = highlight ? highlight.links.has(idx) : false;
          const opacity = highlight ? (isHighlighted ? 0.9 : 0.07) : 0.5;
          return (
            <path
              key={idx}
              d={d}
              stroke={color}
              strokeOpacity={opacity}
              strokeWidth={thickness}
              // Dashed stroke calls out cloaked recipients at a
              // glance — the literal hostname is the customer's,
              // but the request is going to a third-party vendor.
              strokeDasharray={edge.cloaked ? '6 3' : undefined}
              data-testid={`data-flow-edge-${edge.sourceId}-${edge.targetId}`}
              data-dominant-category={edge.dominantCategory}
              data-cloaked={edge.cloaked ? 'true' : 'false'}
              data-highlighted={isHighlighted ? 'true' : 'false'}
            >
              <title>
                {`${edge.weight} requests · ${edge.dominantCategory} · modes: ${Array.from(
                  edge.consentModes,
                ).join(', ')}${edge.cloaked ? ' · cloaked' : ''}`}
              </title>
            </path>
          );
        })}
      </g>
      <g>
        {layout.nodes.map((node) => {
          const isRecipient = node.kind === 'recipient';
          const isPage = node.kind === 'page';
          const isPinned = pinnedId === node.id;
          const isActive = activeId === node.id;
          const inHighlight = highlight ? highlight.nodes.has(node.id) : true;
          const groupOpacity = highlight ? (inHighlight ? 1 : 0.25) : 1;
          // Hover only fires for page nodes; vendors/recipients still
          // accept clicks (vendor card) but don't drive the highlight.
          const handleEnter = isPage ? () => setHoveredId(node.id) : undefined;
          const handleLeave = isPage
            ? () => setHoveredId((cur) => (cur === node.id ? null : cur))
            : undefined;
          return (
            <g
              key={node.id}
              transform={`translate(${node.x0 ?? 0},${node.y0 ?? 0})`}
              onClick={(e) => {
                e.stopPropagation();
                handleNodeClick(node);
              }}
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
              className="cursor-pointer"
              opacity={groupOpacity}
              data-testid={`data-flow-node-${node.id}`}
              data-highlighted={inHighlight ? 'true' : 'false'}
              data-pinned={isPinned ? 'true' : 'false'}
            >
              <rect
                width={(node.x1 ?? 0) - (node.x0 ?? 0)}
                height={Math.max(1, (node.y1 ?? 0) - (node.y0 ?? 0))}
                fill={
                  isRecipient
                    ? '#0f172a'
                    : isPage
                      ? '#64748b'
                      : node.kind === 'more-hops'
                        ? '#94a3b8'
                        : '#334155'
                }
                stroke={isActive ? '#f97316' : 'none'}
                strokeWidth={isActive ? 2 : 0}
              />
              <text
                x={isRecipient ? -4 : (node.x1 ?? 0) - (node.x0 ?? 0) + 4}
                y={((node.y1 ?? 0) - (node.y0 ?? 0)) / 2}
                dy="0.35em"
                textAnchor={isRecipient ? 'end' : 'start'}
                fontSize={10}
                fontWeight={isActive ? 600 : 400}
                fill="#0f172a"
                // Page labels need to participate in hover so reviewers
                // can mouse over the "/ (No consent)" text — the rect
                // alone is 14px and easy to miss. Other labels stay
                // pointer-transparent so accidental brushes through
                // them don't fight with click-to-open-card.
                className={isPage ? 'select-none' : 'pointer-events-none select-none'}
              >
                {truncate(node.label, 36)}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function computeLayout(
  mod: SankeyModule,
  graph: SiteDataFlowGraph,
): SankeyGraph<SiteDataFlowSankeyNode, SankeyLinkInput> {
  const generator = mod
    .sankey<SiteDataFlowSankeyNode, SankeyLinkInput>()
    .nodeId((d) => d.id)
    .nodeAlign(mod.sankeyJustify)
    .nodeWidth(14)
    .nodePadding(10)
    .extent([
      [PADDING, PADDING],
      [WIDTH - PADDING, HEIGHT - PADDING],
    ]);

  return generator({
    nodes: graph.sankeyNodes.map((n) => ({ ...n })),
    links: graph.sankeyLinks.map<SankeyLinkInput>((l) => ({
      source: l.sourceId,
      target: l.targetId,
      value: Math.max(1, l.weight),
      edge: l,
    })),
  });
}
